<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <i class="fas fa-list-alt"></i> Notification Log
                        </h4>
                        <div class="action-btn">
                            <button @click="loadList" class="btn btn-light">
                                <i class="fas fa-sync"></i> Refresh
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Stats -->
            <div class="row mb-3">
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Total Sent (24h)</small>
                        <h3 class="mb-0 text-success">{{ stats.total_sent_24h || 0 }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Total Failed (24h)</small>
                        <h3 class="mb-0 text-danger">{{ stats.total_failed_24h || 0 }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">Email Sent (24h)</small>
                        <h3 class="mb-0 text-primary"><i class="fas fa-envelope"></i> {{ stats.total_email_sent_24h || 0 }}</h3>
                    </div>
                </div>
                <div class="col-md-3">
                    <div class="card p-3 text-center">
                        <small class="text-muted">WA Sent (24h)</small>
                        <h3 class="mb-0 text-success"><i class="fab fa-whatsapp"></i> {{ stats.total_wa_sent_24h || 0 }}</h3>
                    </div>
                </div>
            </div>

            <!-- Filters -->
            <div class="card p-3 mb-3">
                <div class="row">
                    <div class="col-md-2">
                        <label class="small">Event Type</label>
                        <input v-model="filter.event_type" @keyup.enter="loadList" class="form-control form-control-sm" placeholder="otp, package_*, dll">
                    </div>
                    <div class="col-md-2">
                        <label class="small">Channel</label>
                        <select v-model="filter.channel" @change="loadList" class="form-control form-control-sm">
                            <option value="">Semua</option>
                            <option value="email">Email</option>
                            <option value="wa">WhatsApp</option>
                        </select>
                    </div>
                    <div class="col-md-2">
                        <label class="small">Status</label>
                        <select v-model="filter.status" @change="loadList" class="form-control form-control-sm">
                            <option value="">Semua</option>
                            <option value="sent">Sent</option>
                            <option value="failed">Failed</option>
                            <option value="skipped">Skipped</option>
                        </select>
                    </div>
                    <div class="col-md-3">
                        <label class="small">Recipient (email/phone)</label>
                        <input v-model="filter.recipient" @keyup.enter="loadList" class="form-control form-control-sm" placeholder="user@email or 0812...">
                    </div>
                    <div class="col-md-2">
                        <label class="small">User ID</label>
                        <input v-model="filter.user_id" type="number" @keyup.enter="loadList" class="form-control form-control-sm">
                    </div>
                    <div class="col-md-1 d-flex align-items-end">
                        <button @click="loadList" class="btn btn-sm btn-primary btn-block">
                            <i class="fas fa-search"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Table -->
            <div v-if="loading" class="text-center p-5">
                <div class="spinner-border"></div>
            </div>
            <div v-else-if="logs.length === 0" class="card p-5 text-center text-muted">
                <i class="fas fa-inbox fa-3x mb-3" style="color:#d1d5db;"></i>
                <h5>Tidak ada log</h5>
            </div>
            <div v-else class="card p-0" style="overflow-x:auto;">
                <table class="table table-hover mb-0" style="font-size:13px;">
                    <thead style="background:#f9fafb;">
                        <tr>
                            <th>Waktu</th>
                            <th>Event</th>
                            <th>Channel</th>
                            <th>Recipient</th>
                            <th>CC</th>
                            <th>Status</th>
                            <th>Subject / Preview</th>
                            <th>Error</th>
                            <th>User ID</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="log in logs" :key="log.id" :class="{ 'table-danger': log.status === 'failed' }">
                            <td><small>{{ formatTime(log.created_at) }}</small></td>
                            <td><code style="font-size:11px;">{{ log.event_type }}</code></td>
                            <td>
                                <span v-if="log.channel === 'email'" class="badge badge-primary"><i class="fas fa-envelope"></i> email</span>
                                <span v-else-if="log.channel === 'wa'" class="badge badge-success"><i class="fab fa-whatsapp"></i> WA</span>
                                <span v-else class="badge badge-secondary">{{ log.channel }}</span>
                            </td>
                            <td><small>{{ log.recipient }}</small></td>
                            <td><small class="text-muted">{{ log.cc_recipient || '-' }}</small></td>
                            <td>
                                <span v-if="log.status === 'sent'" class="badge badge-success">SENT</span>
                                <span v-else-if="log.status === 'failed'" class="badge badge-danger">FAILED</span>
                                <span v-else class="badge badge-warning">{{ log.status.toUpperCase() }}</span>
                            </td>
                            <td>
                                <small v-if="log.subject"><strong>{{ truncate(log.subject, 60) }}</strong></small>
                                <small v-else class="text-muted">{{ truncate(log.message, 80) }}</small>
                            </td>
                            <td>
                                <small v-if="log.error_message" class="text-danger" :title="log.error_message">
                                    {{ truncate(log.error_message, 50) }}
                                </small>
                                <span v-else class="text-muted">-</span>
                            </td>
                            <td><small>{{ log.user_id || '-' }}</small></td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <!-- Pagination -->
            <div v-if="total > limit" class="d-flex justify-content-between align-items-center mt-3">
                <small class="text-muted">Showing {{ ((page - 1) * limit) + 1 }} - {{ Math.min(page * limit, total) }} of {{ total }}</small>
                <div>
                    <button @click="prevPage" :disabled="page <= 1" class="btn btn-sm btn-light"><i class="fas fa-chevron-left"></i> Prev</button>
                    <span class="mx-2">Page {{ page }} / {{ totalPages }}</span>
                    <button @click="nextPage" :disabled="page >= totalPages" class="btn btn-sm btn-light">Next <i class="fas fa-chevron-right"></i></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
import axios from 'axios';
export default {
    data() {
        return {
            loading: false,
            logs: [],
            total: 0,
            page: 1,
            limit: 30,
            stats: {},
            filter: {
                event_type: '',
                channel: '',
                status: '',
                recipient: '',
                user_id: '',
            },
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.total / this.limit) || 1;
        }
    },
    mounted() {
        this.loadList();
        this.loadStats();
        // Auto-refresh stats every 30s
        this.statsInterval = setInterval(() => this.loadStats(), 30000);
    },
    beforeDestroy() {
        if (this.statsInterval) clearInterval(this.statsInterval);
    },
    methods: {
        async loadList() {
            this.loading = true;
            try {
                const params = { page: this.page, limit: this.limit };
                if (this.filter.event_type) params.event_type = this.filter.event_type;
                if (this.filter.channel) params.channel = this.filter.channel;
                if (this.filter.status) params.status = this.filter.status;
                if (this.filter.recipient) params.recipient = this.filter.recipient;
                if (this.filter.user_id) params.user_id = this.filter.user_id;
                const r = await axios.get('admin/notification-log', { params });
                this.logs = r.data.data.logs || [];
                this.total = r.data.data.total || 0;
            } catch (e) {
                this.$toast.error('Gagal load log: ' + (e.response?.data?.message || e.message));
            }
            this.loading = false;
        },
        async loadStats() {
            try {
                const r = await axios.get('admin/notification-log/stats');
                this.stats = r.data.data.stats || {};
            } catch (e) { /* silent */ }
        },
        prevPage() {
            if (this.page > 1) { this.page--; this.loadList(); }
        },
        nextPage() {
            if (this.page < this.totalPages) { this.page++; this.loadList(); }
        },
        formatTime(ts) {
            if (!ts) return '-';
            return moment(ts).format('DD MMM YYYY HH:mm:ss');
        },
        truncate(s, n) {
            if (!s) return '';
            if (s.length <= n) return s;
            return s.substring(0, n) + '...';
        },
    },
};
</script>

<style scoped>
.badge { padding: 4px 8px; font-size: 10px; font-weight: 600; }
</style>
